import { SubscriptionBoardAPI } from '@business/api/subscription_board';
import { SubscriptionBoardResponse, SubscriptionBoardId } from '@business/entities/marketing';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class SubscriptionBoardBloc extends BaseBloc {
  private readonly subscriptionBoardRepo = new Repository<SubscriptionBoardResponse>({
    getItemId: item => item.id,
  });

  readonly boards$ = this.subscriptionBoardRepo.items$;

  onReset() {
    this.subscriptionBoardRepo.reset();
  }

  fetchSubscriptionBoards = () => {
    return SubscriptionBoardAPI.fetchSubscriptionBoards().pipe(
      this.subscriptionBoardRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_pros_error') }),
    );
  };

  fetchSubscriptionBoard = (id: SubscriptionBoardId) => {
    return SubscriptionBoardAPI.fetchSubscriptionBoard(id).pipe(
      this.subscriptionBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_pros_error') }),
    );
  };

  createSubscriptionBoard = (data: Partial<SubscriptionBoardResponse>) => {
    return SubscriptionBoardAPI.createSubscriptionBoard(data).pipe(
      this.subscriptionBoardRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_subscription_board'),
      }),
    );
  };

  updateSubscriptionBoard = (id: SubscriptionBoardId, data: Partial<SubscriptionBoardResponse>) => {
    return SubscriptionBoardAPI.updateSubscriptionBoard(id, data).pipe(
      this.subscriptionBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_subscription_board'),
      }),
    );
  };

  deleteSubscriptionBoard = (id: SubscriptionBoardId) => {
    return showDeleteConfirmation('Delete Subscription Board', 'Do you really want to remove this board?').pipe(
      switchMap(() => SubscriptionBoardAPI.deleteSubscriptionBoard(id)),
      this.subscriptionBoardRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_subscription_board'),
      }),
    );
  };
}
